/**
 * Functions for persisting and retrieving report data from IndexedDB
 */

import { db, getCacheKey } from './reportDatabase'

/**
 * Store flat report data AND pre-built hierarchy in IndexedDB
 * Stores both for different use cases:
 * - Flat rows: For exports, flexibility, debugging
 * - Pre-built hierarchy: For instant display (no grouping needed)
 *
 * @param {string} pipelineCode - Pipeline identifier
 * @param {object} variables - Query variables
 * @param {string} userId - Current user ID
 * @param {Array} flatRows - Flat array of data rows
 * @param {object} hierarchy - Hierarchy specification
 * @param {string} version - Data version/timestamp
 * @param {Function} onProgress - Progress callback
 */
export async function storeReportData (pipelineCode, variables, userId, flatRows, hierarchy, version = null, onProgress = null) {
  const cacheKey = getCacheKey(pipelineCode, variables)
  const timestamp = Date.now()

  console.debug(`Storing ${flatRows.length} rows in IndexedDB for key: ${cacheKey}`)

  // Clear existing data for this report
  await db.rows.where('pipeline_key').equals(cacheKey).delete()
  await db.hierarchies.where('pipeline_key').equals(cacheKey).delete()

  // Add context fields to each row
  // Only add generic fields - all pipeline-specific columns preserved as-is
  const rowsWithContext = flatRows.map(row => ({
    ...row,
    pipeline_key: cacheKey,
    user_id: userId
  }))

  if (onProgress) {
    onProgress({ stage: 'storing_flat', message: 'Caching results...' })
  }

  // Bulk insert flat rows (very fast - can handle 100k+ rows in seconds)
  await db.rows.bulkAdd(rowsWithContext)

  console.debug('Building and storing pre-built hierarchy...')

  if (onProgress) {
    onProgress({ stage: 'building_hierarchy', message: 'Building complete hierarchy tree...' })
  }

  // Build complete hierarchy tree
  const builtTree = buildCompleteHierarchy(flatRows, hierarchy)
  console.log('Built tree structure:', {
    topLevelGroups: builtTree.length,
    firstGroup: builtTree[0],
    sampleChild: builtTree[0]?.data?.[0]
  })

  if (onProgress) {
    onProgress({ stage: 'storing_hierarchy', message: 'Caching hierarchy tree...' })
  }

  // Store pre-built hierarchy for instant queries
  await db.hierarchies.put({
    key: cacheKey,
    pipeline_key: cacheKey,
    user_id: userId,
    updated_at: timestamp,
    tree_data: builtTree, // Complete hierarchy
    row_count: flatRows.length
  })

  // Store metadata
  await db.metadata.put({
    key: cacheKey,
    pipeline_code: pipelineCode,
    user_id: userId,
    updated_at: timestamp,
    row_count: flatRows.length,
    version: version || timestamp
  })

  console.debug(`Successfully stored ${flatRows.length} rows + ${builtTree.length} top-level groups in hierarchy`)

  return { cacheKey, rowCount: flatRows.length, timestamp }
}

/**
 * Build complete hierarchy from flat rows
 * Processes all data to create full tree structure
 */
function buildCompleteHierarchy (flatRows, hierarchy) {
  return buildChildLevel(flatRows, hierarchy)
}

/**
 * Check if cached data exists and get metadata
 */
export async function getCacheMetadata (pipelineCode, variables, userId) {
  const cacheKey = getCacheKey(pipelineCode, variables)

  const metadata = await db.metadata
    .where('key').equals(cacheKey)
    .and(m => m.user_id === userId)
    .first()

  return metadata || null
}

/**
 * Check if cache is fresh and complete
 * @param {number} maxAge - Maximum age in milliseconds
 */
export async function isCacheFresh (pipelineCode, variables, userId, maxAge = 60 * 60 * 1000) {
  const metadata = await getCacheMetadata(pipelineCode, variables, userId)

  if (!metadata) return false

  // Cache must be complete, not still loading
  if (metadata.status !== 'complete') {
    console.log('Cache exists but not complete, status:', metadata.status)
    return false
  }

  const age = Date.now() - metadata.updated_at
  return age < maxAge
}

/**
 * Query paginated data from IndexedDB
 * @param {string} pipelineCode - Pipeline identifier
 * @param {object} variables - Query variables
 * @param {string} userId - Current user ID
 * @param {object} options - Query options (pagination, sorting, filtering)
 */
export async function queryReportData (pipelineCode, variables, userId, options = {}) {
  const {
    offset = 0,
    limit = 250,
    searchTerm = null,
    sortBy = null,
    sortDesc = false
  } = options

  const cacheKey = getCacheKey(pipelineCode, variables)

  // Start with base query
  let query = db.rows
    .where('pipeline_key').equals(cacheKey)
    .and(row => row.user_id === userId)

  // Apply search filter (across all fields)
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase()
    query = query.filter(row => {
      const rowString = JSON.stringify(Object.values(row)).toLowerCase()
      return rowString.includes(searchLower)
    })
  }

  // Get results
  const results = await query.toArray()

  // Apply sorting (in memory - could optimize with indexes later)
  if (sortBy) {
    results.sort((a, b) => {
      const aVal = a[sortBy]
      const bVal = b[sortBy]

      if (aVal == null) return 1
      if (bVal == null) return -1

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal)
        return sortDesc ? -comparison : comparison
      }

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      return sortDesc ? -comparison : comparison
    })
  }

  // Get total count before pagination
  const totalCount = results.length

  // Apply pagination
  const paginatedResults = results.slice(offset, offset + limit)

  return {
    data: paginatedResults,
    total: totalCount,
    offset,
    limit
  }
}

/**
 * Query pre-built hierarchy from cache (FAST - no grouping needed)
 * @param {string} pipelineCode - Pipeline identifier
 * @param {object} variables - Query variables
 * @param {string} userId - Current user ID
 * @param {object} options - Query options (pagination, search, sort)
 * @returns {object} Paginated tree data
 */
export async function queryPreBuiltHierarchy (pipelineCode, variables, userId, options = {}) {
  const {
    offset = 0,
    limit = 250,
    searchTerm = null,
    sortBy = null,
    sortDesc = false
  } = options

  const cacheKey = getCacheKey(pipelineCode, variables)

  console.time('queryPreBuiltHierarchy')

  // Load pre-built tree from cache
  const cached = await db.hierarchies
    .where('[pipeline_key+user_id]')
    .equals([cacheKey, userId])
    .first()

  console.log('Cached hierarchy lookup:', { cacheKey, userId, found: !!cached })

  if (!cached || !cached.tree_data) {
    console.warn('No pre-built hierarchy found in cache', { cacheKey, userId, cached })
    return { data: [], total: 0, offset, limit }
  }

  let treeData = cached.tree_data
  console.log('Loaded tree data:', { topLevelCount: treeData.length, firstNode: treeData[0] })

  // Apply search filter (if needed)
  if (searchTerm) {
    treeData = filterTreeBySearch(treeData, searchTerm)
  }

  // Apply sorting (if needed)
  if (sortBy) {
    console.log(`Sorting by: ${sortBy}, desc: ${sortDesc}, sample values:`, {
      first: treeData[0]?.[sortBy],
      second: treeData[1]?.[sortBy],
      third: treeData[2]?.[sortBy]
    })
    treeData = sortTree(treeData, sortBy, sortDesc)
    console.log('After sort, sample values:', {
      first: treeData[0]?.[sortBy],
      second: treeData[1]?.[sortBy],
      third: treeData[2]?.[sortBy]
    })
  }

  console.timeEnd('queryPreBuiltHierarchy')

  // Paginate
  const total = treeData.length
  const paginatedData = treeData.slice(offset, offset + limit)

  console.log(`Returning ${paginatedData.length} of ${total} groups from pre-built hierarchy`)

  return {
    data: paginatedData,
    total,
    offset,
    limit
  }
}

/**
 * Filter tree data by search term (recursive)
 * Returns new tree without mutating original
 */
function filterTreeBySearch (treeData, searchTerm) {
  if (!searchTerm) return treeData

  const searchLower = searchTerm.toLowerCase()

  return treeData.map(node => {
    // Check if node itself matches
    const nodeString = JSON.stringify(Object.values(node)).toLowerCase()
    const nodeMatches = nodeString.includes(searchLower)

    // Check if any children match
    let filteredChildren = null
    if (node.data && node.data.length > 0) {
      filteredChildren = filterTreeBySearch(node.data, searchTerm)
    }

    // Include node if it matches OR has matching children
    if (nodeMatches || (filteredChildren && filteredChildren.length > 0)) {
      return {
        ...node,
        data: filteredChildren || node.data // Use filtered children if they exist
      }
    }

    return null
  }).filter(node => node !== null)
}

/**
 * Sort tree data by field
 * Handles strings, numbers, and numeric strings (with commas)
 */
function sortTree (treeData, sortBy, sortDesc) {
  const sorted = [...treeData].sort((a, b) => {
    let aVal = a[sortBy]
    let bVal = b[sortBy]

    if (aVal == null) return 1
    if (bVal == null) return -1

    // Try to parse as number if it's a numeric string (handles decimals)
    if (typeof aVal === 'string') {
      const aNum = parseFloat(aVal)
      if (!isNaN(aNum) && aVal.trim() !== '') {
        aVal = aNum
      }
    }
    if (typeof bVal === 'string') {
      const bNum = parseFloat(bVal)
      if (!isNaN(bNum) && bVal.trim() !== '') {
        bVal = bNum
      }
    }

    // If both are numbers now, compare numerically
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      const comparison = aVal - bVal
      return sortDesc ? -comparison : comparison
    }

    // Otherwise string comparison
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      const comparison = aVal.localeCompare(bVal)
      return sortDesc ? -comparison : comparison
    }

    // Mixed types - numbers before strings
    if (typeof aVal === 'number') return sortDesc ? 1 : -1
    if (typeof bVal === 'number') return sortDesc ? -1 : 1

    const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
    return sortDesc ? -comparison : comparison
  })

  return sorted
}

/**
 * Recursively build child levels from flat items
 * Similar to buildTree but works on a subset of items
 */
function buildChildLevel (items, childSpec) {
  if (!items || items.length === 0 || !childSpec) {
    return []
  }

  const groups = new Map()

  for (const item of items) {
    // Build group key
    const keyParts = childSpec.groupBy.map(field => JSON.stringify(item[field]))
    const key = keyParts.join('|')

    if (!groups.has(key)) {
      groups.set(key, {
        representative: item,
        items: []
      })
    }

    groups.get(key).items.push(item)
  }

  // Convert to result array
  const results = []

  for (const [, group] of groups) {
    const node = {}

    // Extract fields
    if (childSpec.take) {
      for (const [targetKey, sourcePath] of Object.entries(childSpec.take)) {
        if (Array.isArray(sourcePath) && sourcePath.length === 1) {
          const fieldName = sourcePath[0].replace(/^\$\./, '')
          node[targetKey] = group.items
            .map(item => item[fieldName])
            .filter(v => v !== undefined)
        } else {
          const fieldName = typeof sourcePath === 'string'
            ? sourcePath.replace(/^\$\./, '')
            : sourcePath
          node[targetKey] = group.representative[fieldName]
        }
      }
    }

    // Recursively build deeper levels
    if (childSpec.child) {
      node.data = buildChildLevel(group.items, childSpec.child)
    }

    results.push(node)
  }

  return results
}

/**
 * Query hierarchical data - top level only
 * Groups by first level and returns aggregated data
 * OPTIMIZED: Uses streaming to avoid loading all rows into memory
 */
export async function queryTopLevelGroups (pipelineCode, variables, userId, hierarchy, options = {}) {
  const {
    offset = 0,
    limit = 250,
    searchTerm = null,
    sortBy = null,
    sortDesc = false
  } = options

  const cacheKey = getCacheKey(pipelineCode, variables)
  const groupByFields = hierarchy.groupBy

  console.time('queryTopLevelGroups')

  // Build groups using streaming (don't load all into memory)
  const groups = new Map()
  const searchLower = searchTerm?.toLowerCase()

  // Stream through rows instead of loading all at once
  await db.rows
    .where('[pipeline_key+user_id]')
    .equals([cacheKey, userId])
    .each(row => {
      // Apply search filter during streaming (faster!)
      if (searchTerm) {
        const rowString = JSON.stringify(Object.values(row)).toLowerCase()
        if (!rowString.includes(searchLower)) {
          return // Skip non-matching rows
        }
      }

      // Build group key
      const keyParts = groupByFields.map(field => JSON.stringify(row[field]))
      const key = keyParts.join('|')

      // Create or update group
      if (!groups.has(key)) {
        groups.set(key, {
          representative: row,
          items: [],
          key
        })
      }

      groups.get(key).items.push(row)
    })

  console.timeEnd('queryTopLevelGroups')

  // Convert to array
  const groupArray = Array.from(groups.values())

  // Extract fields and build hierarchy FIRST (so mapped names exist for sorting)
  const allResults = groupArray.map(group => {
    const node = {}

    if (hierarchy.take) {
      for (const [targetKey, sourcePath] of Object.entries(hierarchy.take)) {
        if (Array.isArray(sourcePath) && sourcePath.length === 1) {
          // Array aggregation
          const fieldName = sourcePath[0].replace(/^\$\./, '')
          node[targetKey] = group.items
            .map(item => item[fieldName])
            .filter(v => v !== undefined)
        } else {
          // Simple field
          const fieldName = typeof sourcePath === 'string'
            ? sourcePath.replace(/^\$\./, '')
            : sourcePath
          node[targetKey] = group.representative[fieldName]
        }
      }
    }

    // Build child hierarchy if specified
    if (hierarchy.child) {
      node.data = buildChildLevel(group.items, hierarchy.child)
    }

    // Add metadata for hierarchy support
    node._groupKey = group.key
    node._hasChildren = !!hierarchy.child
    node._childCount = group.items.length

    return node
  })

  // Apply sorting AFTER field extraction (so we sort by mapped names like emvValue)
  if (sortBy) {
    allResults.sort((a, b) => {
      const aVal = a[sortBy] // Now this is the mapped field name (emvValue)
      const bVal = b[sortBy]

      if (aVal == null) return 1
      if (bVal == null) return -1

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal)
        return sortDesc ? -comparison : comparison
      }

      const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      return sortDesc ? -comparison : comparison
    })
  }

  const totalGroups = allResults.length
  const results = allResults.slice(offset, offset + limit)

  console.log(`Grouped ${totalGroups} groups from query, returning ${results.length} for page`)

  return {
    data: results,
    total: totalGroups,
    offset,
    limit
  }
}

/**
 * Query children for a specific group
 * Used when expanding a node
 */
export async function queryGroupChildren (pipelineCode, variables, userId, hierarchy, parentKey) {
  const cacheKey = getCacheKey(pipelineCode, variables)
  const groupByFields = hierarchy.groupBy

  // Parse parent key to get filter values
  const parentValues = parentKey.split('|').map(v => JSON.parse(v))

  // Get all rows matching parent
  let query = db.rows
    .where('pipeline_key').equals(cacheKey)
    .and(row => row.user_id === userId)

  // Filter by parent group values
  query = query.filter(row => {
    return groupByFields.every((field, index) => {
      return JSON.stringify(row[field]) === JSON.stringify(parentValues[index])
    })
  })

  const parentRows = await query.toArray()

  // If there's a child spec, group by it
  if (hierarchy.child) {
    const childGroupBy = hierarchy.child.groupBy
    const childGroups = new Map()

    for (const row of parentRows) {
      const keyParts = childGroupBy.map(field => JSON.stringify(row[field]))
      const key = keyParts.join('|')

      if (!childGroups.has(key)) {
        childGroups.set(key, {
          representative: row,
          items: []
        })
      }

      childGroups.get(key).items.push(row)
    }

    // Extract fields
    const results = Array.from(childGroups.values()).map(group => {
      const node = {}

      if (hierarchy.child.take) {
        for (const [targetKey, sourcePath] of Object.entries(hierarchy.child.take)) {
          if (Array.isArray(sourcePath) && sourcePath.length === 1) {
            const fieldName = sourcePath[0].replace(/^\$\./, '')
            node[targetKey] = group.items
              .map(item => item[fieldName])
              .filter(v => v !== undefined)
          } else {
            const fieldName = typeof sourcePath === 'string'
              ? sourcePath.replace(/^\$\./, '')
              : sourcePath
            node[targetKey] = group.representative[fieldName]
          }
        }
      }

      node._hasChildren = !!hierarchy.child.child
      node._childCount = group.items.length

      return node
    })

    return results
  }

  return parentRows
}

/**
 * Initialize cache for incremental loading
 * Marks cache as "loading" and clears any old data
 */
export async function initializeCacheForLoading (pipelineCode, variables, userId, totalRows, totalChunks) {
  const cacheKey = getCacheKey(pipelineCode, variables)

  // Clear old data
  await db.rows.where('pipeline_key').equals(cacheKey).delete()
  await db.hierarchies.where('pipeline_key').equals(cacheKey).delete()

  // Create metadata with loading status
  await db.metadata.put({
    key: cacheKey,
    pipeline_code: pipelineCode,
    user_id: userId,
    updated_at: Date.now(),
    row_count: 0,
    total_rows: totalRows,
    status: 'loading',
    chunks_loaded: 0,
    total_chunks: totalChunks,
    version: Date.now().toString()
  })

  return cacheKey
}

/**
 * Store a single chunk incrementally
 * Updates progress in metadata
 */
export async function storeChunk (pipelineCode, variables, userId, chunkData, chunkIndex) {
  const cacheKey = getCacheKey(pipelineCode, variables)

  // Add context to rows
  const rowsWithContext = chunkData.map(row => ({
    ...row,
    pipeline_key: cacheKey,
    user_id: userId,
    chunk_index: chunkIndex
  }))

  // Store chunk
  await db.rows.bulkAdd(rowsWithContext)

  // Get current metadata to increment counts
  const metadata = await db.metadata.get(cacheKey)
  const newRowCount = (metadata?.row_count || 0) + chunkData.length
  const newChunksLoaded = (metadata?.chunks_loaded || 0) + 1

  // Update progress
  await db.metadata.update(cacheKey, {
    row_count: newRowCount,
    chunks_loaded: newChunksLoaded,
    updated_at: Date.now()
  })

  return { rowCount: newRowCount, chunksLoaded: newChunksLoaded }
}

/**
 * Build and store hierarchy from data already in IndexedDB
 * Reads flat rows from IndexedDB, builds tree, stores tree
 * More memory efficient than building from RAM
 */
export async function storeHierarchyFromIndexedDB (pipelineCode, variables, userId, hierarchy, onProgress = null) {
  const cacheKey = getCacheKey(pipelineCode, variables)

  console.log('Building hierarchy from IndexedDB data...')

  if (onProgress) {
    onProgress({ stage: 'building_hierarchy', message: 'Loading data from IndexedDB to build hierarchy...' })
  }

  // Load all rows from IndexedDB (necessary for hierarchy building)
  const allRows = await db.rows
    .where('[pipeline_key+user_id]')
    .equals([cacheKey, userId])
    .toArray()

  console.log(`Loaded ${allRows.length} rows from IndexedDB, building tree...`)

  if (onProgress) {
    onProgress({ stage: 'building_hierarchy', message: `Building hierarchy from ${allRows.length.toLocaleString()} rows...` })
  }

  // Remove our added fields before building tree
  // eslint-disable-next-line camelcase
  const cleanRows = allRows.map(({ pipeline_key, user_id, chunk_index, ...row }) => row)

  // Build tree
  const builtTree = buildCompleteHierarchy(cleanRows, hierarchy)

  console.log(`Built tree with ${builtTree.length} top-level groups`)

  if (onProgress) {
    onProgress({ stage: 'storing_hierarchy', message: 'Storing pre-built hierarchy...' })
  }

  // Store hierarchy
  await db.hierarchies.put({
    key: cacheKey,
    pipeline_key: cacheKey,
    user_id: userId,
    updated_at: Date.now(),
    tree_data: builtTree,
    row_count: allRows.length
  })

  // Update metadata to complete
  await db.metadata.update(cacheKey, {
    status: 'complete',
    updated_at: Date.now()
  })

  console.log('Hierarchy stored, cache marked complete')

  return { rowCount: allRows.length, groupCount: builtTree.length }
}

/**
 * Delete cached report data (both flat and hierarchy)
 */
export async function deleteCachedReport (pipelineCode, variables, userId) {
  const cacheKey = getCacheKey(pipelineCode, variables)

  await db.rows.where('pipeline_key').equals(cacheKey).delete()
  await db.hierarchies.where('pipeline_key').equals(cacheKey).delete()
  await db.metadata.where('key').equals(cacheKey).delete()

  console.debug(`Deleted cached report: ${cacheKey}`)
}
